import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import axiosConfig from "../../axiosConfig";
import React from "react";
import {
  SUPPORTED_CAD_EXTENSIONS,
  CAD_FILE_ACCEPT_STRING,
  CAD_FILE_EXTENSIONS_DISPLAY,
} from "../../common/constants";
import CadFileIcon from './CadFileIcon';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

export default function SubcontractorUploadFileDialog({
  open,
  onClose,
  ticketId,
  refreshData,
}: {
  open: boolean;
  onClose: () => void;
  ticketId: string;
  refreshData: () => void;
}) {
  const [loading, setLoading] = React.useState(false);
  const [fileName, setFileName] = React.useState("");
  const [fileData, setFileData] = React.useState<string>("");
  const [isDragging, setIsDragging] = React.useState(false);
  
  const handleCloseDialog = () => {
    onClose();
    setFileName("");
    setFileData("");
    setIsDragging(false);
  };
  
  const isValidCADFile = (filename: string): boolean => {
    const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    return SUPPORTED_CAD_EXTENSIONS.includes(extension);
  };

  const processFile = (file: File) => {
    if (!isValidCADFile(file.name)) {
      alert(
        "サポートされていないファイル形式です。\n" +
        `対応形式: ${CAD_FILE_EXTENSIONS_DISPLAY}`
      );
      return;
    }
    
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64String = event.target?.result as string;
      setFileData(base64String);
    };
    reader.readAsDataURL(file);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging on the main container, not child elements
    if (e.currentTarget === e.target || !isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear dragging when leaving the main container
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      processFile(file);
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
      <DialogTitle>CADファイルをアップロード</DialogTitle>
      <DialogContent dividers>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          {/* Single unified layout for both states */}
          <div style={{ width: "100%" }}>
            <div 
              style={{ 
                padding: '40px', 
                textAlign: 'center',
                backgroundColor: isDragging ? '#e3f2fd' : '#f5f5f5',
                borderRadius: '8px',
                border: fileName 
                  ? (isDragging ? '2px dashed #1976d2' : '2px solid #1976d2')
                  : (isDragging ? '2px solid #1976d2' : '2px dashed #1976d2'),
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                position: 'relative'
              }}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {/* Drag overlay - shows when dragging */}
              {isDragging && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(25, 118, 210, 0.15)',
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10,
                  pointerEvents: 'none'
                }}>
                  <CloudUploadIcon 
                    sx={{ 
                      fontSize: 80, 
                      color: '#1565c0',
                      marginBottom: '10px'
                    }} 
                  />
                  <div style={{ fontSize: '20px', fontWeight: 600, color: '#1565c0' }}>
                    {fileName ? 'ここにファイルをドロップして置き換え' : 'ここにファイルをドロップ'}
                  </div>
                </div>
              )}
              
              {/* Main content */}
              <div style={{ 
                opacity: isDragging ? 0.3 : 1,
                transition: 'opacity 0.3s ease',
                pointerEvents: isDragging ? 'none' : 'auto'
              }}>
                {fileName ? (
                  // File selected state
                  <>
                    <CadFileIcon 
                      sx={{ 
                        fontSize: 80, 
                        color: '#1976d2',
                        marginBottom: '20px'
                      }} 
                    />
                    <div style={{ fontSize: '18px', fontWeight: 500, marginBottom: '10px' }}>
                      選択されたCADファイル
                    </div>
                    <div style={{ 
                      fontSize: '16px', 
                      color: '#1976d2',
                      wordBreak: 'break-all',
                      marginBottom: '20px',
                      padding: '0 20px'
                    }}>
                      {fileName}
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#666',
                      backgroundColor: '#fff',
                      padding: '15px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      marginBottom: '20px'
                    }}>
                      <strong>📋 形式:</strong> {fileName.substring(fileName.lastIndexOf('.')).toUpperCase().replace('.', '')}
                    </div>
                    
                    {/* Re-select button */}
                    <input
                      type="file"
                      accept={CAD_FILE_ACCEPT_STRING}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          processFile(file);
                        }
                      }}
                      style={{ display: "none" }}
                      id="re-upload-input"
                    />
                    <label htmlFor="re-upload-input">
                      <Button variant="outlined" component="span" size="small">
                        別のファイルを選択
                      </Button>
                    </label>
                    
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#999',
                      marginTop: '15px'
                    }}>
                      💡 ファイルをここにドラッグして置き換えることもできます
                    </div>
                  </>
                ) : (
                  // No file selected state
                  <>
                    <CloudUploadIcon 
                      sx={{ 
                        fontSize: 80, 
                        color: '#1976d2',
                        marginBottom: '20px'
                      }} 
                    />
                    <div style={{ fontSize: '18px', fontWeight: 500, marginBottom: '10px' }}>
                      CADファイルをアップロード
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#666',
                      marginBottom: '25px'
                    }}>
                      ファイルをドラッグ＆ドロップ または クリックして選択
                    </div>
                    
                    <input
                      type="file"
                      accept={CAD_FILE_ACCEPT_STRING}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          processFile(file);
                        }
                      }}
                      style={{ display: "none" }}
                      id="upload-input"
                    />
                    <label htmlFor="upload-input">
                      <Button 
                        variant="contained" 
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        size="large"
                      >
                        ファイルを選択
                      </Button>
                    </label>
                    
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#999',
                      marginTop: '20px',
                      backgroundColor: '#fff',
                      padding: '12px',
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}>
                      <strong>対応形式:</strong><br />
                      {CAD_FILE_EXTENSIONS_DISPLAY}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleCloseDialog}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          disabled={!fileData || loading}
          onClick={async () => {
            if (!fileData) {
              alert("ファイルを選択してください。");
              return;
            }

            setLoading(true);
            try {
              const base64 = fileData.split(",")[1];
              await axiosConfig.post("/image/sub-upload", {
                image_name: fileName,
                image_data: base64,
                ticket_id: ticketId,
              });

              refreshData();
              handleCloseDialog();
            } catch (error) {
              console.error("Upload error:", error);
              alert("ファイルのアップロードに失敗しました。");
            } finally {
              setLoading(false);
            }
          }}
        >
          {loading ? (
            <CircularProgress
              size={20}
              sx={{
                color: "white",
              }}
            />
          ) : (
            "アップロード"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
